import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Loader2, Sparkles, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  TaskAreaLabels,
  TaskPriorityLabels,
  TaskAreaColors,
  TaskPriorityColors
} from '../../types/task';
import { createTasksFromAI } from '../../services/api';

interface TaskAICreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTasksCreated: (count: number) => void;
  projectId: string;
}

interface AIGeneratedTask {
  name: string;
  description: string;
  area: 'frontend' | 'backend' | 'design' | 'Q&A';
  horasEstimadas: number;
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente';
}

interface AITaskResponse {
  tasks: AIGeneratedTask[];
  totalGenerated: number;
  totalCreated: number;
  creationErrors?: string[];
}

export const TaskAICreationModal: React.FC<TaskAICreationModalProps> = ({
  isOpen,
  onClose,
  onTasksCreated,
  projectId
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [aiResults, setAiResults] = useState<AITaskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File) => {
    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.md')) {
      setError('Por favor, selecione apenas arquivos .md');
      return;
    }

    // Validate file size (10MB limit)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Arquivo muito grande. Limite de 10MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    
    // Read file content for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFileContent(content);
    };
    reader.readAsText(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleProcessFile = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const response = await createTasksFromAI(projectId, file);
      setAiResults(response.data.data);
      onTasksCreated(response.data.data.totalCreated);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao processar arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setFileContent('');
    setAiResults(null);
    setError(null);
    setShowPreview(false);
    setIsUploading(false);
    onClose();
  };

  const handleStartOver = () => {
    setFile(null);
    setFileContent('');
    setAiResults(null);
    setError(null);
    setShowPreview(false);
    setIsUploading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <CardTitle className="text-xl text-foreground-primary">
                Criar Tarefas por IA
              </CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-foreground-tertiary">
            Envie uma transcrição de reunião em formato Markdown e a IA criará tarefas automaticamente
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Results Section */}
          {aiResults && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-green-400 font-medium">
                    {aiResults.totalCreated} tarefa(s) criada(s) com sucesso!
                  </p>
                  {aiResults.creationErrors && aiResults.creationErrors.length > 0 && (
                    <p className="text-orange-400 text-sm">
                      {aiResults.creationErrors.length} erro(s) durante a criação
                    </p>
                  )}
                </div>
              </div>

              {/* Created Tasks List */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground-primary">
                  Tarefas Criadas ({aiResults.tasks.length})
                </h3>
                <div className="grid gap-3 max-h-60 overflow-y-auto">
                  {aiResults.tasks.map((task, index) => (
                    <Card key={index} variant="glass" className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-foreground-primary text-sm">
                            {task.name}
                          </h4>
                          <div className="flex gap-1">
                            <Badge className={`${TaskAreaColors[task.area]} text-xs`}>
                              {TaskAreaLabels[task.area]}
                            </Badge>
                            <Badge className={`${TaskPriorityColors[task.prioridade]} text-xs`}>
                              {TaskPriorityLabels[task.prioridade]}
                            </Badge>
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-xs text-foreground-tertiary">
                            {task.description}
                          </p>
                        )}
                        <p className="text-xs text-foreground-tertiary">
                          Estimativa: {task.horasEstimadas}h
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Creation Errors */}
              {aiResults.creationErrors && aiResults.creationErrors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-orange-400">Erros durante criação:</h4>
                  <div className="space-y-1">
                    {aiResults.creationErrors.map((error, index) => (
                      <p key={index} className="text-xs text-orange-400 bg-orange-500/10 p-2 rounded">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handleStartOver}>
                  Processar outro arquivo
                </Button>
                <Button variant="gradient" onClick={handleClose}>
                  Concluir
                </Button>
              </div>
            </div>
          )}

          {/* File Upload Section */}
          {!aiResults && (
            <>
              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              {/* Upload Area */}
              <div
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                  ${file 
                    ? 'border-green-500/50 bg-green-500/10' 
                    : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
                  }
                `}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".md"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                
                {file ? (
                  <div className="space-y-3">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
                    <div>
                      <p className="text-green-400 font-medium">{file.name}</p>
                      <p className="text-foreground-tertiary text-sm">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-foreground-tertiary mx-auto" />
                    <div>
                      <p className="text-foreground-primary font-medium">
                        Arraste um arquivo .md aqui ou clique para selecionar
                      </p>
                      <p className="text-foreground-tertiary text-sm">
                        Apenas arquivos Markdown (.md) até 10MB
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File Preview */}
              {file && fileContent && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-foreground-primary">
                      Preview do arquivo
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Ocultar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Mostrar
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {showPreview && (
                    <div className="bg-white/5 rounded-lg p-4 max-h-40 overflow-y-auto">
                      <pre className="text-xs text-foreground-tertiary whitespace-pre-wrap">
                        {fileContent.length > 1000 
                          ? `${fileContent.substring(0, 1000)}...\n\n[Arquivo truncado para preview - ${fileContent.length} caracteres total]`
                          : fileContent
                        }
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button
                  variant="gradient"
                  onClick={handleProcessFile}
                  loading={isUploading}
                  loadingText="Processando com IA..."
                  disabled={!file}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Criar Tarefas com IA
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};